import React, { useEffect, useMemo, useState } from "react";
import RoleToggle from "../../components/RoleToggle";
import LineHaul from "./_components/LineHaul";
import Fleet from "./_components/Fleet";
import { useSearchParams } from "react-router-dom";
import { usePermission } from "../../hooks/usePermissionTabs";

const FleetManagement: React.FC = () => {
  const { allowed: canReadVehicles, isLoading: isLoadingVehicles } = usePermission("vehicles", "read");
  const { allowed: canReadLineHaul, isLoading: isLoadingLineHaul } = usePermission("line_haul", "read");
  const { allowed: canReadLineHaulRequest, isLoading: isLoadingLineHaulRequest } = usePermission("line_haul_request", "read");

  const canAccessLineHaul = canReadLineHaul || canReadLineHaulRequest;

  const [searchParams, setSearchParams] = useSearchParams();
  const activeRole = searchParams.get("tab") || "Vehicles";

  const [headerActions, setHeaderActions] = useState<React.ReactNode>(null);
  const [headerButtons, setHeaderButtons] = useState<React.ReactNode>(null);

  const roleOptions = useMemo(() => {
    const opts = [];
    if (canReadVehicles) opts.push("Vehicles");
    if (canAccessLineHaul) opts.push("Line Haul");
    return opts;
  }, [canReadVehicles, canAccessLineHaul]);

  useEffect(() => {
    if (!canReadVehicles && canAccessLineHaul && activeRole === "Vehicles") {
      setSearchParams({ tab: "Line Haul" });
    } else if (canReadVehicles && !canAccessLineHaul && activeRole === "Line Haul") {
      setSearchParams({ tab: "Vehicles" });
    }
  }, [canReadVehicles, canAccessLineHaul, activeRole]);

  const handleRoleChange = (role: string) => {
    setSearchParams({ tab: role });
  };

  const isLoading = isLoadingVehicles || isLoadingLineHaul || isLoadingLineHaulRequest;

  if (isLoading) {
    return (
      <div className="px-3 pb-8 space-y-4">
        <div className="bg-white rounded-xl p-6 h-64 animate-pulse" />
      </div>
    );
  }

  if (roleOptions.length === 0) {
    return (
      <div className="px-3 pb-8">
        <div className="bg-white rounded-xl p-6 text-center text-gray-500 border">
          You do not have permission to access Fleet Management.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="px-3  pb-8">
        <header className="flex flex-col lg:flex-row lg:items-center justify-between w-full gap-4">
          {roleOptions.length > 0 && (
            <RoleToggle
              options={roleOptions}
              active={activeRole}
              onChange={handleRoleChange}
              activeClassName="bg-[#FBD037] text-[#131327] font-bold shadow-sm"
            />
          )}
          <div className="flex flex-wrap items-center gap-2">
            {headerActions}
            {headerButtons}
          </div>
        </header>

        <div>
          {activeRole === "Vehicles" && canReadVehicles && (
            <Fleet
              setHeaderActions={setHeaderActions}
              setHeaderButtons={setHeaderButtons}
            />
          )}
        </div>
        <div>
          {activeRole === "Line Haul" && canAccessLineHaul && (
            <LineHaul
              setHeaderActions={setHeaderActions}
              setHeaderButtons={setHeaderButtons}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default FleetManagement;
